<template>
  <button
    type="button"
    class="color-picker-button"
    :class="{ 'color-picker-button-hover': showHoverEffect }"
    :style="colors"
    data-test-id="color-picker-button"
  >
    <span class="color-picker-button__background"></span>

    <span class="color-picker-button__content" :style="{ color: displayColor }">
      <span
        v-if="circleIcon"
        class="color-picker-button__circle"
        :class="{ 'color-picker-button__circle--no-color': color === null }"
        :style="{ 'background-color': color }"
        data-test-id="color-picker-button-circle"
      />

      <template v-else>
        <ColorDot :color="color" />
      </template>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import tinycolor from 'tinycolor2';
import { colorGrey500 } from '@sass/globals.module.scss';

export default defineComponent({
  name: 'ColorPickerButton',

  props: {
    circleIcon: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: null,
    },
    showHoverEffect: {
      default: true,
      type: Boolean,
    },
  },

  computed: {
    colors(): { '--bg-color'?: string } {
      return {
        '--bg-color': tinycolor(this.color || colorGrey500)
          .setAlpha(0.1)
          .toRgbString(),
      };
    },

    displayColor(): string {
      return this.color || colorGrey500;
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
.color-picker-button {
  position: relative;
  border: none;
  outline: none;
  cursor: pointer;
  height: 32px;
  width: 32px;
  border-radius: settings.$border-radius;
  transition: all 0.1s ease-in-out;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    box-shadow: 0 0 0 3px var(--bg-color);
  }
}

.color-picker-button-hover:hover .color-picker-button__background {
  opacity: 1;
}

.color-picker-button__background {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transition: all 0.1s ease-in-out;
  opacity: 0;

  background: var(--bg-color);
}

.color-picker-button__content {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s ease-in-out;
}

.color-picker-button__circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;

  &--no-color {
    outline: 1px solid colors.$color-grey-500;
  }
}

.color-picker-button__no-color {
  color: colors.$color-grey-500;
}
</style>
